import { z } from "zod";

// Allowlist marker: any field listed here bypasses PII masking.
// Format: "event_name.field_name" or "*.field_name" for global allowlist.
export const FIELD_ALLOWLIST: ReadonlyArray<string> = [
  "*.app_version",
  "*.app_env",
  "*.page_path",
  "*.page_locale",
  "*.device_type",
  "*.is_authenticated",
  "*.session_id",
  "*.anonymous_id",
  "*.timestamp",
  "*.value",
  "*.currency",
  "*.method",
  "*.kind",
  "*.locale",
  "*.results_count",
  "*.item_count",
  // Session events:
  "session_started.referrer_host",
  "session_started.utm_source",
  "session_started.utm_medium",
  "session_started.utm_campaign",
  // Page:
  "page_viewed.current_path",
  "page_viewed.previous_path",
  "page_viewed.title",
  // CTA:
  "cta_clicked.cta_id",
  "cta_clicked.location",
  "cta_clicked.variant",
  // Consent:
  "consent_decision_made.decision",
  "consent_decision_made.categories",
  "consent_decision_made.policy_version",
  // Form failure (kept generic, no field values):
  "form_submission_failed.form_id",
  "form_submission_failed.error_code",
  // Error boundary:
  "error_boundary_triggered.component",
  "error_boundary_triggered.route",
  "error_boundary_triggered.message",
  // Web vitals:
  "web_vital_measured.metric",
  "web_vital_measured.value",
  "web_vital_measured.rating",
  "web_vital_measured.route",
  // Business events: IDs that are NOT user PII (order/product/transaction).
  // transaction_id may be a UUID — masker would otherwise REDACT it.
  "checkout_started.transaction_id",
  "checkout_completed.transaction_id",
  "wallet_topup_started.transaction_id",
  "wallet_topup_completed.transaction_id",
  "order_created.order_id",
  "order_completed.order_id",
  "catalog_product_viewed.product_id",
  "catalog_product_viewed.category",
  "catalog_search_performed.query_hash",
  // Sprint 3 — UX + experimentation
  "rage_click.element",
  "rage_click.clicks",
  "rage_click.route",
  "dead_click.element",
  "dead_click.route",
  "scroll_depth_reached.threshold",
  "scroll_depth_reached.route",
  "form_abandoned.form_id",
  "form_abandoned.fields_touched",
  "form_abandoned.duration_ms",
  "form_abandoned.reason",
  "form_abandoned.route",
  "checkout_step_changed.step",
  "checkout_step_changed.step_index",
  "faq_expanded.faq_id",
  "faq_expanded.route",
  "tooltip_opened.tooltip_id",
  "tooltip_opened.route",
  "experiment_exposed.experiment",
  "experiment_exposed.variant",
  "experiment_exposed.bucket_id_hash",
  "experiment_converted.experiment",
  "experiment_converted.variant",
  "experiment_converted.goal",
];


export interface EventDescriptor<TPayload> {
  payload: z.ZodType<TPayload>;
  consentReplayable: boolean;
  essentialSinksOnly: boolean;
  priority: "high" | "low";
}

function def<T>(
  payload: z.ZodType<T>,
  opts: Partial<Omit<EventDescriptor<T>, "payload">> = {},
): EventDescriptor<T> {
  return {
    payload,
    consentReplayable: opts.consentReplayable ?? true,
    essentialSinksOnly: opts.essentialSinksOnly ?? false,
    priority: opts.priority ?? "low",
  };
}

const CurrencySchema = z.enum(["BRL", "USD", "EUR"]).default("BRL");
const CheckoutKindSchema = z.enum(["wallet", "order", "product", "service", "mass"]);

export const EventSchema = {
  session_started: def(
    z.object({
      referrer_host: z.string().nullable(),
      utm_source: z.string().nullable(),
      utm_medium: z.string().nullable(),
      utm_campaign: z.string().nullable(),
    }),
    { consentReplayable: true },
  ),
  session_ended: def(
    z.object({
      reason: z.enum(["idle", "day_boundary", "visibility_hidden", "explicit"]),
      duration_ms: z.number().int().nonnegative(),
    }),
    { consentReplayable: true },
  ),
  page_viewed: def(
    z.object({
      current_path: z.string(),
      previous_path: z.string().nullable(),
      title: z.string().max(300),
    }),
    { consentReplayable: true },
  ),
  consent_decision_made: def(
    z.object({
      decision: z.enum(["accept_all", "reject_non_essential", "partial"]),
      categories: z.object({
        essential: z.boolean(),
        analytics: z.boolean(),
        marketing: z.boolean(),
        replay: z.boolean(),
      }),
      policy_version: z.string(),
    }),
    { consentReplayable: true, essentialSinksOnly: true },
  ),
  cta_clicked: def(
    z.object({
      cta_id: z.string(),
      location: z.string(),
      variant: z.string().optional(),
    }),
  ),
  form_submission_failed: def(
    z.object({
      form_id: z.string(),
      error_code: z.string(),
    }),
    { consentReplayable: false, essentialSinksOnly: true },
  ),
  error_boundary_triggered: def(
    z.object({
      component: z.string(),
      route: z.string(),
      message: z.string().max(500),
    }),
    { consentReplayable: false, essentialSinksOnly: true, priority: "high" },
  ),
  web_vital_measured: def(
    z.object({
      metric: z.enum(["LCP", "INP", "CLS", "FCP", "TTFB"]),
      value: z.number(),
      rating: z.enum(["good", "needs-improvement", "poor"]),
      route: z.string(),
    }),
    { consentReplayable: true },
  ),

  // ---- Sprint 2: Business events ----
  auth_signup_completed: def(
    z.object({
      method: z.string(),
    }),
    { consentReplayable: true, priority: "high" },
  ),
  auth_login_completed: def(
    z.object({
      method: z.string(),
    }),
    { consentReplayable: true },
  ),
  checkout_started: def(
    z.object({
      kind: CheckoutKindSchema,
      value: z.number().nonnegative(),
      currency: CurrencySchema,
      item_count: z.number().int().nonnegative().optional(),
    }),
    { consentReplayable: false, priority: "high" },
  ),
  checkout_completed: def(
    z.object({
      kind: CheckoutKindSchema,
      transaction_id: z.string(),
      value: z.number().nonnegative(),
      currency: CurrencySchema,
      item_count: z.number().int().nonnegative().optional(),
    }),
    { consentReplayable: false, priority: "high" },
  ),
  wallet_topup_started: def(
    z.object({
      method: z.enum(["stripe", "pix"]),
      value: z.number().nonnegative(),
      currency: CurrencySchema,
    }),
    { consentReplayable: false, priority: "high" },
  ),
  wallet_topup_completed: def(
    z.object({
      method: z.enum(["stripe", "pix"]),
      transaction_id: z.string(),
      value: z.number().nonnegative(),
      currency: CurrencySchema,
    }),
    { consentReplayable: false, priority: "high" },
  ),
  order_created: def(
    z.object({
      order_id: z.string(),
      value: z.number().nonnegative(),
      currency: CurrencySchema,
      item_count: z.number().int().nonnegative(),
    }),
    { consentReplayable: false, priority: "high" },
  ),
  order_completed: def(
    z.object({
      order_id: z.string(),
      value: z.number().nonnegative(),
      currency: CurrencySchema,
    }),
    { consentReplayable: false, priority: "high" },
  ),
  catalog_product_viewed: def(
    z.object({
      product_id: z.string(),
      category: z.string().optional(),
      locale: z.enum(["pt", "en"]).optional(),
    }),
  ),
  catalog_search_performed: def(
    z.object({
      query_hash: z.string(),
      results_count: z.number().int().nonnegative(),
    }),
  ),

  // ---- Sprint 3: UX friction + experimentation ----
  rage_click: def(
    z.object({
      element: z.string().max(200),
      clicks: z.number().int().min(3).max(50),
      route: z.string(),
    }),
    { consentReplayable: true },
  ),
  dead_click: def(
    z.object({
      element: z.string().max(200),
      route: z.string(),
    }),
    { consentReplayable: true },
  ),
  scroll_depth_reached: def(
    z.object({
      threshold: z.union([z.literal(25), z.literal(50), z.literal(75), z.literal(100)]),
      route: z.string(),
    }),
    { consentReplayable: true },
  ),
  form_abandoned: def(
    z.object({
      form_id: z.string().max(120),
      fields_touched: z.number().int().nonnegative(),
      duration_ms: z.number().int().nonnegative(),
      reason: z.enum(["visibility", "navigation", "unload"]),
      route: z.string(),
    }),
    { consentReplayable: true },
  ),
  checkout_step_changed: def(
    z.object({
      kind: CheckoutKindSchema,
      step: z.string().max(60),
      step_index: z.number().int().nonnegative().optional(),
    }),
    { consentReplayable: false, priority: "high" },
  ),
  faq_expanded: def(
    z.object({
      faq_id: z.string().max(120),
      route: z.string(),
    }),
  ),
  tooltip_opened: def(
    z.object({
      tooltip_id: z.string().max(120),
      route: z.string(),
    }),
  ),
  experiment_exposed: def(
    z.object({
      experiment: z.string().max(80),
      variant: z.string().max(80),
      bucket_id_hash: z.string().max(40),
    }),
    { consentReplayable: true, priority: "high" },
  ),
  experiment_converted: def(
    z.object({
      experiment: z.string().max(80),
      variant: z.string().max(80),
      goal: z.string().max(80),
      value: z.number().optional(),
    }),
    { consentReplayable: false, priority: "high" },
  ),
} as const;

export type EventName = keyof typeof EventSchema;
export type EventPayload<E extends EventName> = z.infer<typeof EventSchema[E]["payload"]>;

export function getAllowlistFor(event: string): Set<string> {
  const out = new Set<string>();
  for (const entry of FIELD_ALLOWLIST) {
    const [evt, field] = entry.split(".");
    if (evt === "*" || evt === event) out.add(field);
  }
  return out;
}

// Event names that should be flagged as "conversions" downstream (GA4 / PostHog).
export const CONVERSION_EVENTS: ReadonlySet<EventName> = new Set([
  "auth_signup_completed",
  "checkout_completed",
  "wallet_topup_completed",
  "order_completed",
]);
